import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { Call } from '@twilio/voice-sdk'
import { nanoid } from 'nanoid'
import dayjs from 'dayjs'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { showToast } from 'shared/ui'
import { LINK_ENABLE_MICROPHONE } from 'shared/constants/links'
import { DayjsFormats, logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { CallContact } from 'entities/Call/model/CallContact'
import { Contact } from 'entities/Contacts/model/Contact'
import { type Phone } from 'entities/Phone/model/Phone'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { type PowerDialer } from 'entities/PowerDialer/model/PowerDialer'
import { PowerDialerApi } from 'entities/PowerDialer/api/powerDialer'
import {
  type IResponsePowerDialer,
  type PowerDialerContactFinalStatus,
  PowerDialerStatus,
} from 'entities/PowerDialer/api/types'
import type { IResponseContact } from 'entities/Contacts/api/types'
import { getSmartOptionLabel } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { BuyCreditsModalStore } from 'entities/Billing/ui/BuyCreditsModal'
import { Conversation } from 'entities/Conversation'
import { QuietHours } from 'entities/QuietHours/model'
import { isInQuietHoursTimeframe } from 'entities/QuietHours'
import type { ICallPopUpPowerDialerStoreV2InitData } from 'widgets/CallPopUp/store/types'
import { PowerDialerSessionStore } from 'widgets/CallPopUp/store/powerDialer/powerDialerSessionStore'
import { getToastConfigByPowerDialerStatus } from 'widgets/PowerDialer'
import { EventCallPopUpPowerDialerUpdated } from 'widgets/CallPopUp/events/EventCallPopUpPowerDialerUpdated'

const CALL_DELAY_DEFAULT = 5_000
const KEEP_ALIVE_INTERVAL = 10_000
const CONTACTS_PER_PAGE = 10

export class CallPopUpPowerDialerStoreV2 {
  private _fetchedContactsMap: Map<number, CallContact> = new Map()
  private _awaitingResolutionContactIds: Set<number> = new Set()
  private _callLaterContactIds: Set<number> = new Set()
  private _remainingContactsCount = 0
  private _contactInCallDelay: CallContact | null = null

  private _callFromNumber: Phone | null = null
  private _callFromTeam: Inbox | null = null
  private _powerDialer: PowerDialer | null = null
  private _onRemovePowerDialerCallTeam: (teamId: number) => void = () => {}

  private _isZeroContactsFlowActive = false
  private _isRunning = false
  private _isSaving = false
  private _isPause = false
  private _isPreCallSetupInProgress = false
  private _isCallInProgress = false
  private _isRefreshingPowerDialerContacts = false
  private _isContactsLoading = false

  private _buyCreditsModalStore: BuyCreditsModalStore
  private _powerDialerSessionStore = new PowerDialerSessionStore()

  private _callDelayValue = CALL_DELAY_DEFAULT
  private _callDelayInterval: ReturnType<typeof setInterval> | null = null
  private _keepAliveInterval: ReturnType<typeof setInterval> | null = null

  private _disposeCallStatus: IReactionDisposer | null = null
  private _disposePause: IReactionDisposer | null = null
  private _disposeContacts: IReactionDisposer | null = null
  private _disposePowerDialerFinish: IReactionDisposer | null = null
  private _disposeEventPowerDialerUpdate: (() => void) | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._buyCreditsModalStore = new BuyCreditsModalStore(
      this._entities.subscriptionStore,
      this._entities.organizationStore,
      this._entities.billingStore
    )
    this._initReactions()
  }

  get entities() {
    return this._entities
  }

  get callStore() {
    return this._entities.callStore
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  private _initReactions = () => {
    this._reactionCallStatus()
    this._reactionPause()
    this._reactionContacts()
    this._reactionPowerDialerFinish()
    this._subscribeEventPowerDialerUpdate()
  }

  setPowerDialerInitData = async (options: ICallPopUpPowerDialerStoreV2InitData) => {
    const { number, team, powerDialer, removePowerDialer } = options
    this._initReactions()
    this._callFromNumber = number
    this._callFromTeam = team
    this._powerDialer = powerDialer
    this._onRemovePowerDialerCallTeam = removePowerDialer
    this._startKeepAlive()
    if (this.isNonPowerDialerCallActive) {
      this.setPause(true)
    }

    if (this._powerDialer.status !== PowerDialerStatus.InProgress) {
      const {
        data: { data: powerDialerResponse },
      } = await PowerDialerApi.changePowerDialerStatus(powerDialer.id, PowerDialerStatus.InProgress)

      this._powerDialer.syncOrigin(powerDialerResponse)
    }
    this._isRunning = true
    this._powerDialerSessionStore.setPowerDialerId(options.powerDialer.id)
    this._remainingContactsCount = powerDialer?.statistics?.noContacted ?? 0

    if (this._remainingContactsCount === 0) {
      this._handleZeroContactsFlow(false)
    } else {
      this.fetchNextContacts()
    }
  }

  fetchNextContacts = async () => {
    if (!this.hasUnfetchedContacts || !this._powerDialer) return

    runInAction(() => {
      this._isContactsLoading = true
    })
    try {
      const excludeIds = [
        ...Array.from(this._fetchedContactsMap.keys()),
        ...Array.from(this._awaitingResolutionContactIds),
        ...Array.from(this._callLaterContactIds),
      ]
      const {
        data: { data: responseContacts, meta },
      } = await PowerDialerApi.getPowerDialerContacts(this._powerDialer.id, {
        limit: CONTACTS_PER_PAGE,
        page: 1,
        status: 'not_contacted',
        exclude_ids: excludeIds,
      })

      const isAllMainContactsLoaded =
        !responseContacts?.length || (meta && meta.current_page >= meta.last_page)

      if (responseContacts?.length) {
        this._addContacts(responseContacts)
      }

      if (isAllMainContactsLoaded && this._callLaterContactIds.size > 0 && this._powerDialer) {
        const powerDialerId = this._powerDialer.id
        const excludeIdsWithoutCallLater = [
          ...Array.from(this._fetchedContactsMap.keys()),
          ...Array.from(this._awaitingResolutionContactIds),
        ]
        const {
          data: { data: callLaterContacts },
        } = await PowerDialerApi.getPowerDialerContacts(powerDialerId, {
          limit: CONTACTS_PER_PAGE,
          page: 1,
          status: 'not_contacted',
          exclude_ids: excludeIdsWithoutCallLater,
        })
        if (callLaterContacts?.length) {
          callLaterContacts.forEach((c) => this._callLaterContactIds.delete(c.id))
          this._addContacts(callLaterContacts)
        }
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isContactsLoading = false
      })
      this._setContactInCallDelay()
    }
  }

  private _refreshPowerDialerContacts = async (): Promise<number> => {
    if (!this._powerDialer) return 0
    this._resetContactInCallDelay()
    runInAction(() => {
      this._isRefreshingPowerDialerContacts = true
    })
    try {
      const {
        data: { data: powerDialerResponse },
      } = await PowerDialerApi.getPowerDialerWithUpdatedContactsCount(this._powerDialer.id)
      this.updatePowerDialer(powerDialerResponse)

      showToast({
        type: 'info',
        title: 'Power Dialer contacts refreshed',
      })
      return this._remainingContactsCount
    } catch (e) {
      logger.error(e)
      return 0
    } finally {
      runInAction(() => {
        this._isRefreshingPowerDialerContacts = false
      })
    }
  }

  private _startKeepAlive = () => {
    this._stopKeepAlive()
    if (!this._powerDialer) return

    this._keepAlive()
    this._keepAliveInterval = setInterval(() => {
      this._keepAlive()
    }, KEEP_ALIVE_INTERVAL)
  }

  private _stopKeepAlive() {
    if (!this._keepAliveInterval) return

    clearInterval(this._keepAliveInterval)
    this._keepAliveInterval = null
  }

  private _keepAlive = async () => {
    if (!this._powerDialer) return

    try {
      await PowerDialerApi.keepPowerDialerAlive(this._powerDialer.id)
    } catch (e) {
      logger.error(e)
    }
  }

  private _addContacts = (responseContacts: IResponseContact[]) => {
    responseContacts.forEach((responseContact) => {
      const contact = new Contact(responseContact)
      this._entities.contactsStore.addItem(responseContact)
      this._addContact(contact)
    })
  }

  private _addContact = (contact: Contact) => {
    const item = new CallContact(
      {
        contact: {
          id: contact.id,
          first_name: contact.first_name,
          last_name: contact.last_name,
          full_name: contact.full_name,
          color: contact.color,
          formatted_number: contact.formatted_number || '',
          number: contact.number || '',
        },
        call: {
          sid: '',
        },
      },
      false
    )

    this._fetchedContactsMap.set(item.id, item)
  }

  private _deleteContact = (id: number) => {
    this._fetchedContactsMap.delete(id)
  }

  private _updatePowerDialerStatus = async (status: PowerDialerStatus, withoutToast?: boolean) => {
    if (!this._powerDialer) return
    try {
      const result = await PowerDialerApi.changePowerDialerStatus(this._powerDialer.id, status)
      if (!withoutToast) {
        showToast(getToastConfigByPowerDialerStatus(status))
      }
      return result
    } catch (e) {
      logger.error(e)
    }
  }

  private _resolveContactStatus = async (
    contactId: number,
    status: PowerDialerContactFinalStatus
  ) => {
    if (!this._powerDialer) return
    this._awaitingResolutionContactIds.add(contactId)

    try {
      runInAction(() => {
        this._remainingContactsCount = Math.max(this._remainingContactsCount - 1, 0)
      })
      await PowerDialerApi.resolveContactInPowerDialer(this._powerDialer.id, contactId, status)
    } catch (e) {
      logger.error(e)
    } finally {
      this._awaitingResolutionContactIds.delete(contactId)
    }
  }

  onPowerDialerRefresh = async () => {
    const left = await this._refreshPowerDialerContacts()

    this._entities.eventLogStore.logEvent(
      'Power Dialer Used',
      {
        event_id: 'power_dialer_used',
        action: 'refreshed in PD widget',
      },
      { groupId: this._entities.organizationStore.id }
    )

    runInAction(() => {
      this._isZeroContactsFlowActive = false
    })
    modalStore.closeAllModals()

    if (left === 0) this._handleZeroContactsFlow(false)
  }

  handleSaveAndExit = async () => {
    runInAction(() => {
      this._isSaving = true
    })
    try {
      await this._updatePowerDialerStatus(PowerDialerStatus.Paused)
      this._resetPowerDialer()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  get displayedContactId() {
    return this._contactInCallDelay?.id ?? this.fetchedContacts[0]?.id
  }

  handleSkipContact = async (item: CallContact) => {
    const displayedContactId = this.displayedContactId
    this._resolveContactStatus(item.id, 'skipped')
    this._deleteContact(item.id)
    if (item.id === displayedContactId) {
      this._setContactInCallDelay()
    }
  }

  handleCall = async (callToId: number) => {
    if (!this._callFromTeam) return
    if (!this._powerDialer) return
    if (this.isPowerDialerCallingDisabled) return

    runInAction(() => {
      this._isPreCallSetupInProgress = true
    })
    try {
      if (this.handleCheckCredits()) {
        this.setPause(true)

        return
      }

      this._clearCallDelayTimer()

      this.callStore.setStartedCall(true)
      const isAllowed = await this.callStore.checkMicrophone()

      if (!isAllowed) {
        this.callStore.setStartedCall(false)
        this.setPause(true)
        runInAction(() => {
          this._isPreCallSetupInProgress = false
        })

        showToast({
          type: 'error',
          title: 'Microphone access required',
          desc: 'To enable calling, please allow Salesmsg to access your microphone.',
          action: {
            link: LINK_ENABLE_MICROPHONE,
            text: 'Learn more',
          },
        })

        return
      }

      const conversation = await this.conversationStore.createConversation({
        contact_id: callToId,
        team_id: this._callFromTeam.id,
        number_id: this._callFromNumber?.id,
        smart_option: this._powerDialer.sendFrom.options.smart_option,
      })

      if (conversation instanceof Conversation) {
        runInAction(() => {
          this._isPreCallSetupInProgress = false
          this._isCallInProgress = true
        })
        await this.callStore.connectTwilio(conversation.id, { powerDialerId: this._powerDialer.id })
      }
    } catch (e) {
      logger.error(e)

      this._restartCallDelayTimer()
    } finally {
      runInAction(() => {
        this._isPreCallSetupInProgress = false
      })
    }
  }

  handleCallLater = async (item: CallContact) => {
    if (!this._fetchedContactsMap.has(item.id)) return

    const displayedContactId = this.displayedContactId
    this._fetchedContactsMap.delete(item.id)
    this._callLaterContactIds.add(item.id)

    if (item.id === displayedContactId) {
      this._setContactInCallDelay()
    }
  }

  togglePowerDialerPause = () => {
    this.setPause(!this._isPause)

    this._entities.eventLogStore.logEvent(
      'Power Dialer Used',
      {
        event_id: 'power_dialer_used',
        action: this._isPause ? 'FE Paused' : 'FE Resumed',
      },
      { groupId: this._entities.organizationStore.id }
    )
  }

  handleCheckCredits = () => {
    const trialCredits = this._entities.organizationStore.trialCredits
    const accountCredits = this._entities.organizationStore.accountCredits
    const isTrial = this._entities.subscriptionStore.isTrial

    if (isTrial && trialCredits <= 0) {
      showToast({
        title: 'Your organization is out of message credits',
        type: 'error',
      })

      return true
    }

    if (!isTrial && accountCredits <= 0) {
      if (this._entities.billingStore.autorecharge?.is_active) return false

      this._buyCreditsModalStore.openModal()

      return true
    }

    return false
  }

  setPause = (value: boolean) => {
    this._isPause = value
  }

  updatePowerDialer = (powerDialerResponse: IResponsePowerDialer) => {
    this._powerDialer?.syncOrigin(powerDialerResponse)
    runInAction(() => {
      this._remainingContactsCount = this._powerDialer?.statistics?.noContacted ?? 0
    })

    if (this._remainingContactsCount === 0) {
      this._handleZeroContactsFlow(false)
    } else {
      this._fetchedContactsMap.clear()
      this._callLaterContactIds.clear()
      this.fetchNextContacts()
    }
  }

  handlePowerDialerStop = () => {
    const id = nanoid()
    this._clearCallDelayTimer()

    modalStore.addModal({
      id,
      type: ModalTypeList.ALERT,
      title: 'Are you sure?',
      desc: "If stopped you won't be able to continue the session. Pause to resume later from the Power Dialer campaigns page.",
      primaryAction: {
        text: 'Stop anyway',
        onAction: async () =>
          await this._updatePowerDialerStatusFromModal(PowerDialerStatus.Finished),
      },
      additionalSecondaryAction: {
        text: 'Pause and save',
        onAction: async () =>
          await this._updatePowerDialerStatusFromModal(PowerDialerStatus.Paused),
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          this._restartCallDelayTimer()
          modalStore.closeAllModals()
        },
      },
      zIndex: ZIndex.OVERLAY_TOP_1,
    })
  }

  private _handleZeroContactsFlow = (canRefresh: boolean) => {
    if (this._isZeroContactsFlowActive) return
    this._isZeroContactsFlowActive = true

    if (this.isOnlyManualSelection) {
      this._updatePowerDialerStatus(PowerDialerStatus.Finished, true)
      this._resetPowerDialer()
      return
    }

    const id = nanoid()
    this._clearCallDelayTimer()
    modalStore.addModal({
      id,
      type: ModalTypeList.INFO,
      showCloseButton: false,
      showCloseIcon: false,
      disabledOverlayClose: true,
      disabledOnEscClose: true,
      title: 'No contacts left in this Power Dialer campaign:',
      subtitle: `”${this.currentPowerDialerName}”`,
      desc: canRefresh
        ? 'You can pause to resume later, stop the campaign, or refresh to check for new contacts.'
        : 'You can pause to resume later, or stop the campaign.',
      primaryAction: {
        text: 'Pause campaign',
        onAction: async () => {
          await this._updatePowerDialerStatusFromModal(PowerDialerStatus.Paused)
          runInAction(() => {
            this._isZeroContactsFlowActive = false
          })
        },
      },
      secondaryAction: canRefresh
        ? {
            text: 'Refresh contacts',
            onAction: async () => {
              await this.onPowerDialerRefresh()
            },
          }
        : undefined,
      additionalSecondaryAction: {
        text: 'Stop campaign',
        onAction: async () => {
          await this._updatePowerDialerStatusFromModal(PowerDialerStatus.Finished)
          runInAction(() => {
            this._isZeroContactsFlowActive = false
          })
        },
      },
      zIndex: ZIndex.OVERLAY_TOP_1,
    })
  }

  private _updatePowerDialerStatusFromModal = async (status: PowerDialerStatus) => {
    await this._updatePowerDialerStatus(status)

    modalStore.closeAllModals()
    this._resetPowerDialer()
  }

  private _resetContactInCallDelay = () => {
    this._contactInCallDelay = null
    this._clearCallDelayTimer()
  }

  private _setContactInCallDelay = () => {
    const callCandidate =
      this.hasFetchedContactsToCall && !this.isPowerDialerCallingDisabled && !this._isPause
        ? this.fetchedContacts[0]
        : null

    this._contactInCallDelay = callCandidate
    if (!callCandidate) {
      this._clearCallDelayTimer()
      return
    }
    if (this.isShowQuietHoursContent) {
      this._contactInCallDelay = null
      this._clearCallDelayTimer()
      return
    }
    this._restartCallDelayTimer()
  }

  private _restartCallDelayTimer = () => {
    this._clearCallDelayTimer()
    if (!this._contactInCallDelay) return

    this._callDelayValue = CALL_DELAY_DEFAULT
    const delayedContactId = this._contactInCallDelay.id
    this._callDelayInterval = setInterval(() => {
      runInAction(() => {
        this._callDelayValue -= 1000

        if (this._callDelayValue <= 0) {
          this._clearCallDelayTimer()
          if (!this.isShowQuietHoursContent) {
            this.handleCall(delayedContactId)
          } else {
            this._setContactInCallDelay()
          }
        }
      })
    }, 1000)
  }

  private _clearCallDelayTimer = () => {
    if (!this._callDelayInterval) return

    clearInterval(this._callDelayInterval)
    this._callDelayInterval = null
  }

  private _reactionCallStatus = () => {
    this._disposeCallStatus?.()
    this._disposeCallStatus = reaction(
      () => this.callStore.status,
      (status) => {
        const currentCallId = this.callStore.contactTo?.id
        if (!this._powerDialer) return
        if (this.isNonPowerDialerCallActive) {
          return this.setPause(true)
        }
        if (
          status === Call.State.Ringing &&
          currentCallId &&
          this._fetchedContactsMap.has(currentCallId)
        ) {
          this._resolveContactStatus(currentCallId, 'contacted')
          this._resetContactInCallDelay()
          this._deleteContact(currentCallId)
        }
        if (status === Call.State.Closed) {
          this._isCallInProgress = false
          if (currentCallId && this._fetchedContactsMap.has(currentCallId)) {
            this._resolveContactStatus(currentCallId, 'contacted')
            this._deleteContact(currentCallId)
          }
          if (this.hasFetchedContactsToCall) {
            this._setContactInCallDelay()
          }
        }
      }
    )
  }

  private _reactionPause = () => {
    this._disposePause?.()
    this._disposePause = reaction(
      () => this.isPause,
      (value) => {
        if (!this._powerDialer) return
        if (value) {
          this._resetContactInCallDelay()
        } else {
          this._setContactInCallDelay()
        }
      }
    )
  }

  private _reactionContacts = () => {
    this._disposeContacts?.()
    this._disposeContacts = reaction(
      () => this.fetchedContactsCount,
      async (contactsCount) => {
        if (contactsCount < CONTACTS_PER_PAGE && this.hasUnfetchedContacts) {
          return this.fetchNextContacts()
        }
      }
    )
  }

  private _reactionPowerDialerFinish = () => {
    this._disposePowerDialerFinish?.()
    this._disposePowerDialerFinish = reaction(
      () => [this._remainingContactsCount, this._isPowerDialerCalling],
      ([remainingContactsCount, isPowerDialerCalling]) => {
        if (
          remainingContactsCount === 0 &&
          (!isPowerDialerCalling || this.isNonPowerDialerCallActive || this.isOnlyManualSelection)
        ) {
          this._handleZeroContactsFlow(true)
        }
      }
    )
  }

  private _subscribeEventPowerDialerUpdate = () => {
    this._disposeEventPowerDialerUpdate?.()
    this._disposeEventPowerDialerUpdate = EventCallPopUpPowerDialerUpdated.subscribe(
      this,
      this._entities.websocket
    )
  }

  private get _isPowerDialerCalling() {
    return this._isPreCallSetupInProgress || this._isCallInProgress
  }

  get isNonPowerDialerCallActive() {
    return this.callStore.isBusy && !this._isPowerDialerCalling
  }

  get isPreCallSetupInProgress() {
    return this._isPreCallSetupInProgress
  }

  get contactInCallDelay() {
    return this._contactInCallDelay
  }

  get currentPowerDialerId() {
    return this._powerDialer?.id
  }

  get isOnlyManualSelection() {
    return !!this._powerDialer?.isOnlyManualSelection
  }

  get currentPowerDialerName() {
    return this._powerDialer?.name || 'Power Dialer'
  }

  get lastContactsRefreshed() {
    return this._powerDialer?.lastRefreshed ?? ''
  }

  get fetchedContacts() {
    return Array.from(this._fetchedContactsMap.values())
  }

  get isContactsLoading() {
    return this._isContactsLoading
  }

  get fetchedContactsCount() {
    return this._fetchedContactsMap.size
  }

  get callDelayValue() {
    return dayjs(this._callDelayValue).format('s')
  }

  get isPause() {
    return this._isPause
  }

  get isSaving() {
    return this._isSaving
  }

  get isRefreshingPowerDialerContacts() {
    return this._isRefreshingPowerDialerContacts
  }

  get remainingContactsCount() {
    return this._remainingContactsCount
  }

  get hasFetchedContactsToCall() {
    return this._fetchedContactsMap.size > 0
  }

  get hasUnfetchedContacts() {
    return this.remainingContactsCount > this.fetchedContactsCount
  }

  get isPowerDialerRunning() {
    return this._isRunning
  }

  get isPowerDialerCallingDisabled() {
    return this._isPowerDialerCalling || this.callStore.isBusy
  }

  get callFromNumber() {
    return this._callFromNumber
  }

  get callFromSmartOption() {
    return getSmartOptionLabel(this._powerDialer?.sendFrom.options.smart_option)
  }

  get callFromTeam() {
    return this._callFromTeam
  }

  private _resetPowerDialer = () => {
    const teamId = this._callFromTeam?.id

    this._stopKeepAlive()
    this._clearCallDelayTimer()

    this._disposeCallStatus?.()
    this._disposePause?.()
    this._disposeContacts?.()
    this._disposePowerDialerFinish?.()
    this._disposeEventPowerDialerUpdate?.()

    this._remainingContactsCount = 0
    this._fetchedContactsMap.clear()
    this._awaitingResolutionContactIds.clear()
    this._callLaterContactIds.clear()
    this._contactInCallDelay = null
    this._isZeroContactsFlowActive = false

    this._callFromNumber = null
    this._callFromTeam = null
    this._powerDialer = null

    this._isRunning = false
    this._isPause = false
    this._isSaving = false
    this._isPreCallSetupInProgress = false
    this._isCallInProgress = false
    this._isContactsLoading = false
    this._isRefreshingPowerDialerContacts = false

    this._powerDialerSessionStore.reset()

    this._callDelayValue = CALL_DELAY_DEFAULT
    this._callDelayInterval = null
    this._keepAliveInterval = null

    if (this.callStore.isBusy || !teamId) return

    this._onRemovePowerDialerCallTeam(teamId)
  }

  get quietHours() {
    return new QuietHours(this._entities.organizationStore.quietHoursSettings)
  }

  get isQuietHoursEnabled() {
    return this.quietHours.isQuietHoursEnabled
  }

  get contact() {
    const contactToCall = this._contactInCallDelay || this.fetchedContacts[0]
    if (!contactToCall) {
      return undefined
    }

    return this._entities.contactsStore.getItem(contactToCall.id)
  }

  get contactName() {
    return this.contact?.first_name || this.contact?.last_name
  }

  get contactQuietHoursName() {
    return this.contactName || 'this contact'
  }

  get contactTimezone() {
    return this.contact?.timezone || this.contact?.time_zone
  }

  get localContactTime() {
    return dayjs.utc().tz(this.contactTimezone).format(DayjsFormats.time2)
  }

  get isShowQuietHoursContent() {
    if (!this.isQuietHoursEnabled) {
      return false
    }

    if (!this.contactTimezone?.length) {
      return false
    }

    if (this._isCallInProgress) {
      return false
    }

    return isInQuietHoursTimeframe(
      this.contactTimezone,
      this.quietHours.quietHoursAllowedTimeFrom,
      this.quietHours.quietHoursAllowedTimeTo
    )
  }
}
